import type {
    Appointment,
    ClinicSettings,
    FollowUp,
    Invoice,
    MessageKind,
    Patient
} from '@/types/clinic'

import { formatDate, formatTime } from '@/lib/dates'
import { formatCurrency, normalizePhone } from '@/lib/format'
import { getInvoiceSummary } from '@/lib/clinic/selectors'

export interface TemplateContext {
    patient: Patient | null
    appointment?: Appointment | null
    followUp?: FollowUp | null
    invoice?: Invoice | null
    settings: ClinicSettings
}

export const buildTemplateValues = ({ patient, appointment, followUp, invoice, settings }: TemplateContext): Record<string, string> => {
    const balance = invoice ? getInvoiceSummary(invoice).balance : 0
    const date = appointment?.date ?? followUp?.dueDate ?? ''

    return {
        '[Patient Name]': patient?.name ?? '',
        '[Date]': date ? formatDate(date) : '',
        '[Time]': appointment?.time ? formatTime(appointment.time) : '',
        '[Amount]': invoice ? formatCurrency(balance, settings.billing.currency) : '',
        '[Treatment]': appointment?.treatmentName ?? followUp?.treatment ?? '',
        '[Clinic Name]': settings.clinic.name,
        '[Clinic Phone]': settings.clinic.phone
    }
}

export const fillTemplate = (template: string, values: Record<string, string>): string => {
    return template.replace(/\[(Patient Name|Date|Time|Amount|Treatment|Clinic Name|Clinic Phone)\]/g, match => {
        const value = values[match]
        return value ? value : match
    })
}

export const hasUnfilledPlaceholders = (body: string): boolean => {
    return /\[(Patient Name|Date|Time|Amount|Treatment|Clinic Name|Clinic Phone)\]/.test(body)
}

export const getTemplateBody = (kind: MessageKind, context: TemplateContext): string => {
    if (kind === 'custom') {
        return context.patient ? `Hello ${context.patient.name}, ` : ''
    }
    return fillTemplate(context.settings.whatsapp.templates[kind] ?? '', buildTemplateValues(context))
}

export const buildWhatsAppLink = (phone: string, countryCode: string, body: string): string => {
    const code = countryCode.replace(/\D/g, '') || '91'
    return `https://wa.me/${code}${normalizePhone(phone)}?text=${encodeURIComponent(body)}`
}
